import { MinimumRequirementsInstructions } from './MinimumRequirementsInstructions';
import {
  FieldError,
  FieldsConsumerAggregateError,
  hasFieldsConsumerAggregateError,
} from './models';
import { generateId } from '@/app/core/utils';
import {
  FaExclamationTriangle,
  FaInfoCircle,
  FaThumbsUp,
} from 'react-icons/fa/index.js';
import { Box, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { Alert, Modal } from '@ssar-app/uikit';
import { ReactNode } from 'react';

export interface ErrorsAndWarningReporterProps {
  errors: string[];
  isSSNMapped: boolean;
  hideSuccessMessages?: boolean;
  fieldErrors: FieldError[];
}

const buildErrorNode = (
  inputNode: ReactNode,
  key: string = generateId(),
): ReactNode => {
  return (
    <Alert
      key={key}
      className='consumer-field-error'
      type='error'
      variant='left-accent'
      description={
        <HStack color={'red'}>
          <FaExclamationTriangle />
          {inputNode}
        </HStack>
      }
      descriptionProps={{ display: 'flex', gap: 3, alignItems: 'center' }}
      background={'white'}
      py={1}
      isOpen
      showCloseButton={false}
      showIcon={false}
    ></Alert>
  );
};

const buildSuccessNode = (
  inputNode: ReactNode,
  key: string = generateId(),
): ReactNode => {
  return (
    <Alert
      key={key}
      className='consumer-field-success'
      type='success'
      variant='left-accent'
      description={
        <HStack color={'green'}>
          <FaThumbsUp />
          {inputNode}
        </HStack>
      }
      descriptionProps={{ display: 'flex', gap: 3, alignItems: 'center' }}
      background={'white'}
      py={1}
      isOpen
      showCloseButton={false}
      showIcon={false}
    ></Alert>
  );
};

export const ErrorsAndWarningReporter = (
  props: ErrorsAndWarningReporterProps,
) => {
  const { errors, fieldErrors, isSSNMapped } = props;
  const dispFieldErors = fieldErrors?.filter((error) => error.message);
  const nodes: ReactNode[] = [];
  const hasAddressCombinationsError = hasFieldsConsumerAggregateError(
    errors,
    FieldsConsumerAggregateError.ConsumerAddressCombinations,
  );
  const hasAddressIncompleteness = hasFieldsConsumerAggregateError(
    errors,
    FieldsConsumerAggregateError.ConsumerAddressCompleteness,
  );
  const hasAddressMinimumsError = hasFieldsConsumerAggregateError(
    errors,
    FieldsConsumerAggregateError.ConsumerAddressMinimums,
  );
  const hasNameCombinationsError = hasFieldsConsumerAggregateError(
    errors,
    FieldsConsumerAggregateError.ConsumerNameCombinations,
  );
  const hasNameMinimumsError = hasFieldsConsumerAggregateError(
    errors,
    FieldsConsumerAggregateError.ConsumerNameMinimums,
  );

  if (
    !hasAddressCombinationsError &&
    !hasAddressMinimumsError &&
    !hasAddressIncompleteness
  ) {
    !props.hideSuccessMessages &&
      nodes.push(
        buildSuccessNode(
          <Text>Valid Address minimums/combinations found.</Text>,
        ),
      );
  } else if (hasAddressCombinationsError || hasAddressMinimumsError) {
    const errors: string[] = [];
    if (hasAddressCombinationsError) {
      errors.push('Invalid address combinations found');
    }
    if (hasAddressMinimumsError) {
      errors.push('Missing minimum address fields');
    }
    const finalErrorMessage = errors.join(' and ');
    nodes.push(buildErrorNode(<Text>{finalErrorMessage}</Text>));
  } else {
    nodes.push(buildErrorNode(<Text>Incomplete Address Fields</Text>));
  }

  if (!hasNameCombinationsError && !hasNameMinimumsError) {
    !props.hideSuccessMessages &&
      nodes.push(
        buildSuccessNode(<Text>Valid Name minimums/combinations found.</Text>),
      );
  } else {
    const errors: string[] = [];
    if (hasNameCombinationsError) {
      errors.push('Invalid name combinations found');
    }
    if (hasNameMinimumsError) {
      errors.push('Missing minimum name fields');
    }
    const finalErrorMessage = errors.join(' and ');
    nodes.push(buildErrorNode(<Text>{finalErrorMessage}</Text>));
  }
  if (!isSSNMapped) {
    nodes.push(
      <Alert
        className='consumer-field-warning'
        key='ssn-warning'
        type='warning'
        variant='left-accent'
        description={
          <HStack color={'orange.700'}>
            <FaInfoCircle />
            <Text>
              Please map the SSN field if provided to get a better match rate.
            </Text>
          </HStack>
        }
        descriptionProps={{ display: 'flex', gap: 3, alignItems: 'center' }}
        background={'white'}
        py={1}
        isOpen
        showCloseButton={false}
        showIcon={false}
      ></Alert>,
    );
  }

  if (dispFieldErors.length > 0) {
    nodes.push(
      ...dispFieldErors.map((dispError) =>
        buildErrorNode(<Text>{dispError.message}</Text>),
      ),
    );
  }
  return (
    <VStack>
      {nodes.map((a) => a)}
      {nodes.length > 0 && (
        <Box fontStyle='italic' w='full'>
          Please select Experian field labels for consumer identifying data
          elements. Click{' '}
          <Link
            href='#/minimum-requirements'
            fontWeight={'bold'}
            textDecoration={'underline'}
            color={'blue'}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              Modal.info({
                header: 'Minimum Field Requirements',
                icon: null,
                contentProps: { maxW: '60%' },
                scrollBehavior: 'outside',
                headerProps: { textColor: 'black', fontSize: 18, pt: 6 },
                bodyProps: { pl: 12, pt: 1 },
                content: <MinimumRequirementsInstructions />,
              });
            }}
          >
            here
          </Link>{' '}
          for minimum requirements.
        </Box>
      )}
    </VStack>
  );
};
